/* eslint-disable no-nested-ternary */
'use client';

import React from 'react';
import { builder } from '@builder.io/react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useState, useRef, useEffect, useId, useCallback, useMemo, memo } from 'react';
import Script from 'next/script';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import sanitize from '../shared/sanitize';
import safeUrl from '../shared/safeUrl';
import { useVimeoForceAutoplay } from '../shared/useVimeoForceAutoplay';

import { getOffering, getAmountRaised } from 'services/offerings';
import useGetRewards from 'hooks/useGetRewards';
import useLoginModal from 'hooks/useLoginModal';
import useCanadianFeatureFlag from 'hooks/useCanadianFeatureFlag';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import {
  investNowClick,
  trackEvent,
  trackSEEvent,
  appendPostHogDistinctId,
  ADD_TO_CART_SURFACES,
} from 'utils/analytics';
import {
  isOverAmountRaised,
  resolveOfferingInvestCtaText,
  resolveCurrentAmountRaised,
  resolveCurrentInvestorCount,
} from 'utils/offerings/helpers';
import useRegDExcludedList from 'hooks/useRegDExcludedList';
import { userMenu } from 'utils/links';
import { getEnv, formatCurrencyFlexible } from 'utils/helpers';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import {
  firePlacementViewed,
  firePlacementClicked,
  PLACEMENT_IDS,
  PLACEMENT_TYPES,
  PLACEMENT_SURFACES,
  CLICK_TARGETS,
} from 'utils/placements';

import {
  HeroContainer,
  ContentWrapper,
  LeftContent,
  RightContent,
  RaisedBanner,
  InvestButtonWrapper,
  PriceInfo,
  DisclaimerText,
  VideoWrapper,
  VideoOverlay,
  UnmuteButton,
  StatsGrid,
  StatCard,
} from './styles';
import { useBuilderABTest } from '../../BuilderABTestContext';
import useCustomFont from '../shared/useCustomFont';
import { getBackgroundGradient } from '../shared/backgroundStyles';
import { sectionBorderRadiusSx } from '../shared/sectionBorderRadius';
import useIsHydrated from '../shared/useIsHydrated';
import { COLORS } from '../../designTokens';
import { isTtwOffering } from '../shared/offeringStatus';
import CriteriaBadge from 'components/Offering/HeroSection/CriteriaBadge/CriteriaBadge';
import useIsMobile from 'hooks/useIsMobile';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import OfferingLegalText from 'components/Offering/OfferingLegalText';

const VIMEO_HASH_RE = /^[a-zA-Z0-9]+$/;
const DEFAULT_HERO_MEDIA_ASPECT_RATIO = 16 / 9;

// Builder's Style tab applies its background color to the outer `.builder-block`
// wrapper. Keep the hero transparent by default so that authored color remains
// visible in the control experience. An explicitly configured component-level
// `backgroundColor` still wins through the prop below.
export const DEFAULT_HERO_BACKGROUND_COLOR = 'transparent';

// The StartEngine campaign's published Builder entry briefly inherited
// Atombeam's authored headline and description. Keep this campaign tied to
// the offering API so Builder preview, experiments, and the live page cannot
// surface stale copy from the entry. Other campaigns retain their authored
// Builder copy unless it is empty/default, matching the existing behavior.
const FORCE_DYNAMIC_HERO_COPY_SLUGS = new Set(['startengine']);

export const resolveBuilderHeroCopy = ({
  slug,
  useDynamicData,
  dynamicValue,
  authoredValue,
  isAuthoredValueEmpty,
}) => {
  const forceDynamicCopy = FORCE_DYNAMIC_HERO_COPY_SLUGS.has(String(slug || '').toLowerCase());

  return useDynamicData && dynamicValue && (forceDynamicCopy || isAuthoredValueEmpty)
    ? dynamicValue
    : authoredValue;
};

export const resolveBuilderHeroHeading = ({
  showEyebrow,
  eyebrowText,
  useDynamicData,
  offeringName,
}) => {
  const usesDynamicEyebrow = Boolean(showEyebrow && !eyebrowText && useDynamicData && offeringName);

  return {
    eyebrowComponent: usesDynamicEyebrow ? 'h1' : undefined,
    titleComponent: usesDynamicEyebrow ? 'h2' : 'h1',
  };
};

export const renderHeroDisclaimerContent = (
  primaryDisclaimer,
  additionalDisclaimerText,
  disclaimerColor,
) => (
  <Box data-cy="hero-disclaimers">
    {primaryDisclaimer}
    {additionalDisclaimerText && (
      <DisclaimerText
        component="div"
        role="note"
        data-cy="hero-additional-disclaimer"
        sx={{ color: disclaimerColor, paddingTop: 0, marginTop: 1 }}
        dangerouslySetInnerHTML={{ __html: sanitize(additionalDisclaimerText) }}
      />
    )}
  </Box>
);

/**
 * Extract `{ videoId, hash }` from a Vimeo URL or bare ID.
 *
 * Accepts:
 *   - bare numeric IDs ("1187552526")
 *   - player.vimeo.com/video/{id}[/{hash}] URLs
 *   - vimeo.com/video/{id}[/{hash}] URLs
 *   - vimeo.com/{id}[/{hash}] URLs (with or without share-link query strings
 *     like `?share=copy&fl=sv&fe=ci` or `?h=<hash>`)
 *
 * `hash` is Vimeo's unlisted-video privacy hash. Without it, the player URL
 * `https://player.vimeo.com/video/{id}` is rejected by Vimeo for unlisted
 * videos and the iframe stays blank — which is one of the failure modes the
 * "video doesn't show up sometimes" report tracks back to. Returning the
 * hash here lets every embed builder thread `h=<hash>` through correctly.
 *
 * Returns `{ videoId: '', hash: '' }` for any input that doesn't yield a
 * valid ID. Exported for unit testing and for `buildUnmutedHeroVimeoEmbedUrl`
 * / `buildHeroVimeoBackgroundUrl` below.
 */
export const parseVimeoUrl = (input) => {
  if (!input) return { videoId: '', hash: '' };
  const trimmed = input.toString().trim();

  if (/^\d+$/.test(trimmed)) {
    return { videoId: trimmed, hash: '' };
  }

  const pathHashPatterns = [
    /player\.vimeo\.com\/video\/(\d+)\/([a-zA-Z0-9]+)/,
    /vimeo\.com\/video\/(\d+)\/([a-zA-Z0-9]+)/,
    /vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/,
  ];
  for (const pattern of pathHashPatterns) {
    const match = trimmed.match(pattern);
    if (match?.[1] && match[2] && VIMEO_HASH_RE.test(match[2])) {
      return { videoId: match[1], hash: match[2] };
    }
  }

  const idPatterns = [
    /player\.vimeo\.com\/video\/(\d+)/,
    /vimeo\.com\/video\/(\d+)/,
    /vimeo\.com\/(\d+)/,
  ];
  for (const pattern of idPatterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) {
      const hashMatch = trimmed.match(/[?&]h=([a-zA-Z0-9]+)/);
      const hash = hashMatch?.[1] && VIMEO_HASH_RE.test(hashMatch[1]) ? hashMatch[1] : '';
      return { videoId: match[1], hash };
    }
  }

  return { videoId: '', hash: '' };
};

/**
 * @deprecated Prefer `parseVimeoUrl` to also recover the unlisted hash. Kept
 * for backwards-compatibility with existing imports / tests.
 */
export const getVimeoVideoId = (input) => parseVimeoUrl(input).videoId;

const appendHash = (params, hash) => {
  if (hash) params.set('h', hash);
  return params;
};

/**
 * Build the Vimeo embed URL used as the hero's *initial* iframe `src` when
 * `videoBackgroundMode` is on.
 *
 * Uses Vimeo's `background=1` mode rather than the previous
 * `autoplay=1&muted=1&loop=1&controls=0&...` URL. Per the JSDoc on
 * `useVimeoForceAutoplay`, plain `autoplay=1` is silently blocked even when
 * muted — especially when a second Vimeo iframe sits on the same page (the
 * `BuilderVideoEmbed` block on this same offering page is the typical
 * culprit) — whereas `background=1` is the only Vimeo configuration that
 * autoplays reliably in modern browsers.
 *
 * For non-background mode, falls back to a plain `autoplay=0` URL so Vimeo
 * shows its standard play button. Returns '' when the input doesn't yield a
 * video ID.
 */
export const buildHeroVimeoBackgroundUrl = (vimeoUrl, { videoBackgroundMode = true } = {}) => {
  const { videoId, hash } = parseVimeoUrl(vimeoUrl);
  if (!videoId) return '';

  if (videoBackgroundMode) {
    const params = appendHash(new URLSearchParams({ background: '1', playsinline: '1' }), hash);
    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
  }

  const params = appendHash(new URLSearchParams({ autoplay: '0' }), hash);
  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

/**
 * Build the Vimeo embed URL used by `handleUnmuteClick` when the user taps
 * the unmute overlay on a hero playing in `videoBackgroundMode`.
 *
 * PD-34273: iOS Safari requires autoplay-with-sound to happen inside a
 * single user-gesture tick. Calling `player.setMuted(false)` + `play()` via
 * Vimeo's postMessage API costs multiple async round-trips and Safari drops
 * the gesture before sound starts. Reloading the iframe with `muted=0`
 * already in the URL keeps the gesture chain to a single sync DOM mutation,
 * which Safari accepts. Returns '' when the input doesn't yield a video ID.
 */
export const buildUnmutedHeroVimeoEmbedUrl = (vimeoUrl) => {
  const { videoId, hash } = parseVimeoUrl(vimeoUrl);
  if (!videoId) return '';
  const params = appendHash(
    new URLSearchParams({
      autoplay: '1',
      muted: '0',
      loop: '1',
      controls: '0',
      title: '0',
      byline: '0',
      portrait: '0',
      playsinline: '1',
    }),
    hash,
  );
  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

/**
 * Compute the `sx` overrides applied to the hero `VideoWrapper` for a given
 * source-video aspect ratio (width / height).
 *
 * Returns `null` while the ratio is unknown (the wrapper keeps its default
 * 16:9 framing). For landscape and square sources, applies the natural ratio
 * at full container width — matching the prior behavior for 16:9 videos.
 *
 * For portrait sources (e.g. AquiPor's 9:16 hero, PD-34612), filling the full
 * container width would push the wrapper to ~viewport-height tall on mobile.
 * Instead we clamp the wrapper's *height* and let `aspect-ratio` derive the
 * width — so a 9:16 video on a 410px mobile viewport renders at roughly
 * 281×500 (centered) rather than the previous 390×219 letterbox.
 *
 * Exported for unit testing.
 */
export const resolveHeroVideoSizing = (aspectRatio) => {
  if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return null;
  }
  if (aspectRatio >= 1) {
    return { aspectRatio: `${aspectRatio}` };
  }
  return {
    aspectRatio: `${aspectRatio}`,
    width: 'auto',
    height: { xs: 'min(70vh, 600px)', md: 'min(70vh, 720px)' },
    maxWidth: '100%',
    mx: 'auto',
  };
};

export const resolveHeroImageSizing = (aspectRatio) => {
  if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return null;
  }

  if (aspectRatio >= DEFAULT_HERO_MEDIA_ASPECT_RATIO) {
    return null;
  }

  return { aspectRatio: `${aspectRatio}` };
};

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    if (!targetDate) return;
    const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
    if (Number.isNaN(target.getTime())) return;
    const tick = () => {
      const diff = Math.max(0, target.getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return timeLeft;
};

const CountdownUnit = memo(({ value, label, background, color }) => (
  <Box
    sx={{
      textAlign: 'center',
      borderRadius: '8px',
      px: 1,
      py: 0.75,
      minWidth: '40px',
      background,
    }}
  >
    <Typography
      sx={{
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 1,
        color,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: "'owners-wide', sans-serif",
      }}
    >
      {String(value).padStart(2, '0')}
    </Typography>
    <Typography
      sx={{
        fontSize: '9px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        mt: 0.25,
        color: 'rgba(255,255,255,0.5)',
      }}
    >
      {label}
    </Typography>
  </Box>
));

const BuilderHeroSection = ({
  sectionId = '',
  sectionBorderRadius = '0px',
  titleFontSource = 'default',
  titleAdobeFontName = 'owners-wide',
  titleGoogleFontName = 'Playfair Display',
  titleGoogleFontCustom = '',
  titleGoogleFontWeights = '400;500;700',
  titleCustomFontUrl = '',
  titleCustomFontFamily = '',
  bodyFontSource = 'default',
  bodyAdobeFontName = 'owners-text',
  bodyGoogleFontName = 'Open Sans',
  bodyGoogleFontCustom = '',
  bodyGoogleFontWeights = '400;500;700',
  bodyCustomFontUrl = '',
  bodyCustomFontFamily = '',
  showRaisedBanner = true,
  useDynamicRaisedAmount = true,
  showStatusBadges = true,
  manualStatusBadges = [],
  raisedBannerManualText = '$XXX,XXX,XXX Raised',
  raisedBannerSuffixText = 'Raised',
  raisedBannerBackgroundColor = COLORS.primary,
  raisedBannerTextColor = '#FFFFFF',
  raisedBannerGradient = '',
  raisedBannerPadding = '12px 32px 12px 24px',
  raisedBannerMarginTop = '48px',
  raisedBannerMarginBottom = '48px',
  raisedBannerMarginTopMobile = '',
  raisedBannerMarginBottomMobile = '',
  raisedBannerFontSize = '14px',
  raisedBannerFontWeight = 600,
  raisedBannerLetterSpacing = '0.02em',
  raisedBannerBorderRadius = '0 999px 999px 0',
  raisedBannerPosition = 'top',
  backgroundColor = DEFAULT_HERO_BACKGROUND_COLOR,
  backgroundGradient = '',
  backgroundGradientEnabled = false,
  backgroundGradientType = 'linear',
  backgroundGradientDirection = 'to bottom',
  backgroundGradientAngle = 180,
  backgroundGradientStops = [],
  backgroundImage = '',
  backgroundOverlayColor = 'rgba(0, 0, 0, 0.5)',
  showEyebrow = true,
  eyebrowText = '',
  eyebrowColor = COLORS.primary,
  eyebrowFontSize = '16px',
  eyebrowFontWeight = 400,
  eyebrowLetterSpacing = '2px',
  eyebrowTextTransform = 'none',
  eyebrowMarginBottom = 8,
  title = 'Your Headline Here',
  titleColor = '#111827',
  titleFontFamily = "'owners-text', 'IBM Plex Sans', sans-serif",
  titleFontSize = '34px',
  titleMobileFontSize = '',
  titleFontWeight = 700,
  titleLineHeight = '1.15',
  titleHighlight = '',
  titleHighlightColor = COLORS.primary,
  titleSuperscript = '',
  subtitle = 'Your company description goes here. Explain what makes your offering unique and why investors should be interested.',
  subtitleColor = 'rgba(0,0,0,0.7)',
  subtitleFontFamily = "'IBM Plex Sans', sans-serif",
  subtitleFontSize = '14px',
  subtitleFontWeight = 500,
  subtitleLineHeight = '1.6',
  showBullets = false,
  bullets = [],
  bulletItems = [],
  bulletIconColor = COLORS.primary,
  bulletTextColor = COLORS.primary,
  investButtonText = 'Get Shares Now',
  waitlistButtonText = 'Join the Waitlist',
  waitlistButtonBackgroundColor = '#E06004',
  investButtonBackgroundColor = COLORS.accentOrange,
  reserveButtonBackgroundColor = '#E06004',
  investButtonTextColor = '#FFFFFF',
  investButtonBorderRadius = '8px',
  investButtonFontSize = '16px',
  investButtonFontWeight = 600,
  investButtonWidth = '200px',
  investButtonPadding = '12px 32px',
  useDynamicData = true,
  forceReserveCta = false,
  showPriceInfo = true,
  showSharePrice = true,
  showMinInvestment = true,
  sharePriceLabel = 'Share Price',
  minInvestmentLabel = 'Min Investment',
  priceInfoColor = '#111827',
  manualSharePrice = '$0.00',
  manualMinInvestment = '$100',
  showDisclaimer = true,
  disclaimerPrefix = 'Offered pursuant to',
  disclaimerLinkText = 'Reg CF',
  disclaimerLinkUrl = '',
  disclaimerRegType = 'Reg CF',
  disclaimerRegTypeLink = '',
  disclaimerBrokerName = 'StartEngine Primary, LLC',
  disclaimerBrokerLink = 'https://www.startengine.com',
  disclaimerColor = 'rgba(0,0,0,0.45)',
  disclaimerLinkColor = COLORS.primary,
  additionalDisclaimerText = '',
  showVideo = false,
  showHeroImage = true,
  vimeoUrl = '',
  videoThumbnail = '',
  useDynamicHeroImage = true,
  videoBorderRadius = '16px',
  videoFrameWidth = '12px',
  videoFrameColor = '#FFFFFF',
  videoFrameShadow = '0px 4px 10px rgba(0, 0, 0, 0.1)',
  mediaWidthPercent = 50,
  videoBackgroundMode = true,
  showUnmuteButton = true,
  unmuteButtonPosition = 'center',
  showStatsGrid = true,
  statsGridItems = [
    {
      label: 'Previously Crowdfunded',
      value: '$XXX',
      dataKey: 'previouslyCrowdfunded',
      hideWhenEmpty: true,
    },
    { label: 'Amount Raised', value: '$XXX', dataKey: 'currentRaise', hideWhenEmpty: true },
    { label: 'Investors', value: 'XXX', dataKey: 'investorCount', hideWhenEmpty: true },
    { label: 'Valuation', value: '$XXX', dataKey: 'valuation', hideWhenEmpty: true },
  ],
  statsGridBackgroundColor = 'rgba(24,92,101,0.06)',
  statsGridBorderColor = 'transparent',
  statsGridLabelColor = 'rgba(0,0,0,0.5)',
  statsGridValueColor = '#111827',
  showEarlyBird = true,
  earlyBirdBadgeText = '⏳ Early Bird',
  earlyBirdText = 'Invest now for',
  earlyBirdHighlight = '25% Bonus Shares',
  earlyBirdDeadline = '',
  earlyBirdBackground = `linear-gradient(135deg, ${COLORS.primary}, #1a7a86)`,
  earlyBirdBadgeBackground = 'rgba(1,209,184,0.2)',
  earlyBirdBadgeColor = '#5ef5e0',
  earlyBirdTextColor = 'rgba(255,255,255,0.75)',
  earlyBirdHighlightColor = '#FFFFFF',
  earlyBirdCountdownBackground = 'rgba(255,255,255,0.12)',
  earlyBirdCountdownColor = '#FFFFFF',
  earlyBirdSortStrategy = 'highestBonus',
  maxWidth = 1280,
  contentPadding = '32px 24px 64px',
  contentPaddingMobile = '16px 16px 32px',
  contentGapMobile = '20px',
  contentGapDesktop = '48px',
  contentTopSpacing = '0px',
}) => {
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const [showFullSubtitle, setShowFullSubtitle] = useState(false);
  const [subtitleOverflows, setSubtitleOverflows] = useState(false);
  const subtitleRef = useRef(null);
  const [getTillOfferingClose] = useOfferingDaysLeft();

  const { fontFamily: titleFont } = useCustomFont({
    fontSource: titleFontSource,
    adobeFontName: titleAdobeFontName,
    googleFontName: titleGoogleFontName,
    googleFontCustom: titleGoogleFontCustom,
    googleFontWeights: titleGoogleFontWeights,
    customFontUrl: titleCustomFontUrl,
    customFontFamily: titleCustomFontFamily,
  });

  const { fontFamily: bodyFont } = useCustomFont({
    fontSource: bodyFontSource,
    adobeFontName: bodyAdobeFontName,
    googleFontName: bodyGoogleFontName,
    googleFontCustom: bodyGoogleFontCustom,
    googleFontWeights: bodyGoogleFontWeights,
    customFontUrl: bodyCustomFontUrl,
    customFontFamily: bodyCustomFontFamily,
  });

  const effectiveTitleFont =
    titleFontSource !== 'default' ? titleFont : `var(--builder-title-font, ${titleFontFamily})`;
  const effectiveBodyFont =
    bodyFontSource !== 'default' ? bodyFont : `var(--builder-body-font, ${subtitleFontFamily})`;
  const backgroundGradientValue = getBackgroundGradient({
    backgroundGradient,
    backgroundGradientEnabled,
    backgroundGradientType,
    backgroundGradientDirection,
    backgroundGradientAngle,
    backgroundGradientStops,
  });
  const hasGradient = Boolean(backgroundGradientValue);

  const slug = params?.slug?.[0];
  const abTest = useBuilderABTest();

  const { isEnabled: isCanadianFeatureEnabled } = useCanadianFeatureFlag();
  const isCanadianRoute = isCanadianFeatureEnabled && pathname?.startsWith('/private-canada/');

  const { data: offeringData } = useQuery({
    queryKey: ['offeringData', slug],
    queryFn: () => getOffering({ slug }),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: Boolean(useDynamicData && slug),
    placeholderData: (prev) => prev,
  });
  const { data: heroImageData } = useQuery({
    queryKey: ['heroImageData', slug],
    queryFn: () => getOffering({ slug }),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: Boolean(useDynamicHeroImage && slug && !useDynamicData),
  });

  const { data: amountRaisedData } = useQuery({
    queryKey: ['amountRaised', offeringData?.id],
    queryFn: () => getAmountRaised({ offeringId: offeringData?.id }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    enabled: Boolean(useDynamicRaisedAmount && offeringData?.id),
  });
  const currentAmountRaised = resolveCurrentAmountRaised({
    amountRaisedObject: amountRaisedData,
    offeringData,
  });
  const currentAmountRaisedNumber = Number.parseFloat(currentAmountRaised || 0);
  const currentInvestorCount = resolveCurrentInvestorCount({
    amountRaisedObject: amountRaisedData,
    offeringData,
  });

  const { data: SHOW_AMOUNT_RAISED_LIST } = useShowAmountRaisedList();
  const { data: EXCLUDE_AMOUNT_OFFERING_LIST, isSuccess: loadedExcluded } =
    useExcludeAmountOfferingList();
  const { data: REGD_EXCLUDED_LIST, isSuccess: loadedRegD } = useRegDExcludedList();

  const shouldShowAmount =
    SHOW_AMOUNT_RAISED_LIST?.includes(offeringData?.slug) &&
    !EXCLUDE_AMOUNT_OFFERING_LIST?.includes(offeringData?.slug);
  const regDExclusion = loadedRegD ? !REGD_EXCLUDED_LIST?.includes(offeringData?.slug) : true;
  const hideValue = loadedExcluded
    ? EXCLUDE_AMOUNT_OFFERING_LIST?.includes(offeringData?.slug)
    : true;
  const hideRaised =
    (offeringData?.regulationType === 'Reg D' && regDExclusion) ||
    offeringData?.regulationType === '506(c)' ||
    currentAmountRaisedNumber < 124000;
  const hideInvestors = hideRaised && regDExclusion;
  const canShowAmountRaised =
    !useDynamicData || !offeringData ? true : shouldShowAmount || (!hideRaised && !hideValue);

  const { data: rewardsData } = useGetRewards({
    offeringId: offeringData?.id,
    enabled: Boolean(showEarlyBird && useDynamicData),
  });

  const earlyBirdReward = useMemo(() => {
    if (!Array.isArray(rewardsData) || rewardsData.length === 0) return null;
    const eligible = rewardsData
      .filter((r) => !r.neverEnding && r.endDate)
      .filter((r) => !r.forSeOwnerOnly && !r.forTtwOnly)
      .filter((r) => new Date(r.endDate).getTime() > Date.now());
    if (eligible.length === 0) return null;

    const sortFns = {
      highestBonus: (a, b) =>
        (Number.parseFloat(b.percentBonusShares) || 0) -
        (Number.parseFloat(a.percentBonusShares) || 0),
      lowestMin: (a, b) =>
        (Number.parseFloat(a.minimumAmount) || 0) - (Number.parseFloat(b.minimumAmount) || 0),
      highestMin: (a, b) =>
        (Number.parseFloat(b.minimumAmount) || 0) - (Number.parseFloat(a.minimumAmount) || 0),
      soonestExpiring: (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
    };
    const sortFn = sortFns[earlyBirdSortStrategy] || sortFns.highestBonus;
    eligible.sort(sortFn);
    return eligible[0];
  }, [rewardsData, earlyBirdSortStrategy]);

  const resolvedEarlyBirdDeadline = earlyBirdDeadline || earlyBirdReward?.endDate || '';
  const resolvedEarlyBirdHighlight =
    earlyBirdHighlight === '25% Bonus Shares' && earlyBirdReward?.percentBonusShares
      ? `${earlyBirdReward.percentBonusShares}% Bonus Shares`
      : earlyBirdHighlight;

  const user = useSelector((state) => state.user);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);

  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [redirectUrl, setRedirectUrl] = useState('');

  const isOverRaised = isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist;
  const partner = searchParams?.get('partner');
  const connectRequestId = searchParams?.get('connectRequestId')?.replaceAll('/', '');
  const isRegD =
    offeringData?.regulationType === 'Reg D' || offeringData?.regulationType === '506c';

  // Test-the-Waters offerings show "Reserve Now" on the hero CTA when
  // dynamic data is enabled — regardless of any custom `investButtonText`
  // the issuer set in Builder. Historically the OBM "Testing the Waters"
  // status flag automatically rewrote the hero button to "Reserve Now";
  // that link was lost when the Builder hero gained its own configurable
  // button text. Restoring parity with the sticky sidebar / mobile bar /
  // floating bubble.
  //
  // `isTtwOffering` only auto-detects TTW for the four unambiguous V2
  // statuses (`testing`, `test_the_waters`, `pre_launch`, `ttw`). It
  // also excludes Reg D, so Consensys-style Reg D + `not_submitted`
  // still resolves to "Get Equity".
  //
  // `forceReserveCta` is the manual issuer/admin override for
  // `not_submitted` Reg CF / Reg A+ offerings where the BE doesn't
  // expose a TTW signal yet — gacw-reg-a (GACW Reg A+) is the
  // canonical case: status `not_submitted`, no `ttwDate`, no
  // `numberOfHoursLeft`, but the disclosure text confirms it's a
  // genuine Reg A+ Test-The-Waters preview Danny needs to ship.
  // Toggle ON in Builder.io to flip the CTA + redirect path without
  // waiting on a backend signal. Defaults to OFF so Heartstone /
  // Cancervax draft previews keep the safe green CTA.
  const isTTW = useDynamicData && (isTtwOffering(offeringData) || Boolean(forceReserveCta));

  const resolvedButtonText = useMemo(() => {
    if (isOverRaised) return waitlistButtonText;
    if (isTTW) return 'Reserve Now';
    if (!useDynamicData) return investButtonText;
    return resolveOfferingInvestCtaText({
      offeringData,
      configuredText: investButtonText,
    });
  }, [isOverRaised, waitlistButtonText, isTTW, useDynamicData, offeringData, investButtonText]);
  const isTesting = offeringData?.status === 'testing';
  const isQPOnly = offeringData?.targetAudienceAccreditation === 'QP';

  const formatCurrency = useCallback((value, decimals = 2) => {
    if (!value) return '$0.00';
    try {
      const num = typeof value === 'string' ? Number.parseFloat(value) : value;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
    } catch {
      return `$${value}`;
    }
  }, []);

  const sharePriceValue = useMemo(() => {
    if (!useDynamicData) return manualSharePrice;
    if (!offeringData?.pricePerShare) return '$0.00';
    return formatCurrency(offeringData.pricePerShare);
  }, [useDynamicData, manualSharePrice, offeringData?.pricePerShare, formatCurrency]);

  const minInvestmentValue = useMemo(() => {
    if (!useDynamicData) return manualMinInvestment;
    if (!offeringData?.minimumInvestmentAmount) return '$100';
    return formatCurrency(offeringData.minimumInvestmentAmount);
  }, [useDynamicData, manualMinInvestment, offeringData?.minimumInvestmentAmount, formatCurrency]);

  const countdown = useCountdown(
    showEarlyBird && resolvedEarlyBirdDeadline ? resolvedEarlyBirdDeadline : null,
  );

  const dynamicRaisedBannerText = useMemo(() => {
    if (!useDynamicRaisedAmount) return raisedBannerManualText;

    const amountRaised = currentAmountRaised;

    if (!amountRaised) return raisedBannerManualText;

    const formattedAmount = formatCurrencyFlexible(Number.parseFloat(amountRaised));
    return `${formattedAmount} ${raisedBannerSuffixText}`;
  }, [useDynamicRaisedAmount, raisedBannerManualText, raisedBannerSuffixText, currentAmountRaised]);

  const getInvestUrl = useCallback(
    (isReserved = false) => {
      if (investmentAccountStatus?.investmentAccountFrozen) {
        return userMenu.portfolio;
      }

      let url = '';
      const envPrefix = getEnv();

      if (isReserved) {
        url = `${envPrefix}investment.startengine.com/${slug}/reserve`;
      } else if (
        typeof window !== 'undefined' &&
        window?.location?.hash === '#enforceInvestmentAccount'
      ) {
        url = `${envPrefix}investment.startengine.com/${slug}?action=enforceInvestmentAccount`;
      } else {
        url = `${envPrefix}investment.startengine.com/${slug}`;
      }

      if (partner && connectRequestId) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}partner=${partner}&connectRequestId=${connectRequestId}`;
      }

      return url;
    },
    [slug, partner, connectRequestId, investmentAccountStatus],
  );

  const hasFiredPlacementViewedRef = useRef(false);
  useEffect(() => {
    if (hasFiredPlacementViewedRef.current) return;
    if (!slug) return;
    hasFiredPlacementViewedRef.current = true;
    firePlacementViewed({
      placement_id: PLACEMENT_IDS.CAMPAIGN_PAGE_HERO,
      placement_type: PLACEMENT_TYPES.HERO,
      placement_offering_slug: slug,
      surface: PLACEMENT_SURFACES.CAMPAIGN_PAGE,
      position: 1,
    });
  }, [slug]);

  const handleInvestClick = useCallback(async () => {
    // Block clicks on draft / not-yet-submitted offerings, but let live
    // TTW campaigns through — same carve-out as the bubble. Once the
    // hero CTA was wired through `isTtwOffering` (PR #4274), legitimate
    // Reg CF / Reg A+ TTW offerings whose `status` is `not_submitted`
    // started rendering "Reserve Now" with `getInvestUrl(isTTW)`
    // pointing at `/reserve`, but this guard kept blocking the click
    // handler. The hero button has no `href` fallback either, so the
    // CTA looked correct but did nothing (Devin review on PR #4275).
    if (offeringData?.status === 'not_submitted' && !isTTW) return;

    if (investmentAccountStatus?.investmentAccountFrozen) {
      window.location.href = userMenu.portfolio;
      return;
    }

    if (isCanadianRoute) {
      window.location.href = `/private-canada/invest?offering=${slug}`;
      return;
    }

    const url = getInvestUrl(isTTW);

    if (offeringData) {
      investNowClick({ offeringData, user, surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE });
      trackSEEvent(
        INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow,
        offeringData?.slug,
        user,
      ).catch(() => {});
      trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData).catch(() => {});
      abTest?.trackInvestClick({ offering_slug: slug, section_name: 'hero' });
      firePlacementClicked({
        placement_id: PLACEMENT_IDS.CAMPAIGN_PAGE_HERO,
        placement_type: PLACEMENT_TYPES.HERO,
        placement_offering_slug: offeringData?.slug ?? slug,
        surface: PLACEMENT_SURFACES.CAMPAIGN_PAGE,
        position: 1,
        click_target: CLICK_TARGETS.CTA_BUTTON,
      });
    }

    if (!user?.signedIn || (needToLogin && !partnerLoading)) {
      setRedirectUrl(url);
      setLoginModalOpen(true);
      return;
    }

    if (typeof window !== 'undefined') {
      const fullUrl = `${url}${window.location.search || ''}`;
      window.location.href = appendPostHogDistinctId(fullUrl);
    }
  }, [
    offeringData,
    user,
    setLoginModalOpen,
    needToLogin,
    partnerLoading,
    getInvestUrl,
    investmentAccountStatus,
    slug,
    isCanadianRoute,
    abTest,
    isTTW,
  ]);

  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [vimeoScriptLoaded, setVimeoScriptLoaded] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [heroImageAspectRatio, setHeroImageAspectRatio] = useState(null);
  // `videoStarted` is the click-to-play gate for non-background Vimeo
  // heroes. When `videoBackgroundMode=false` we render an issuer-
  // controlled poster (the campaign cover image) with our own play
  // button overlay instead of mounting Vimeo's iframe in its
  // small-by-default idle state. The user's tap flips this to true,
  // which mounts the iframe with `autoplay=1` baked into the URL —
  // the same fresh-iframe-on-gesture pattern from PD-34273, so iOS
  // Safari accepts autoplay-with-sound on the first try.
  // (Greenfield Robotics, Jun 2026.)
  const [videoStarted, setVideoStarted] = useState(false);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const uniqueId = useId();
  const iframeId = `vimeo-hero-${uniqueId.replace(/:/g, '')}`;

  const resolvedVimeoUrl = showVideo
    ? vimeoUrl || (useDynamicData ? offeringData?.videoUrl : '') || ''
    : '';

  // Once the user clicks our custom play overlay, swap the iframe URL
  // to one with `autoplay=1` baked in. Vimeo's player boots on the
  // same user-gesture tick the iframe is mounted, so iOS allows the
  // autoplay (with sound) and we don't need a separate `setMuted` /
  // `play` postMessage round-trip.
  const heroEmbedUrl = useMemo(() => {
    if (videoStarted && !videoBackgroundMode) {
      const { videoId, hash } = parseVimeoUrl(resolvedVimeoUrl);
      if (!videoId) return '';
      const params = new URLSearchParams({ autoplay: '1', playsinline: '1' });
      if (hash) params.set('h', hash);
      return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
    }
    return buildHeroVimeoBackgroundUrl(resolvedVimeoUrl, { videoBackgroundMode });
  }, [resolvedVimeoUrl, videoBackgroundMode, videoStarted]);

  // Force Vimeo to actually play the background video via the Player JS SDK
  // (PD-34417). URL params alone are unreliable when a second Vimeo iframe
  // (the `BuilderVideoEmbed` block) loads on the same page: browsers throttle
  // the second concurrent autoplay attempt and the hero silently stays on
  // its first frame. The SDK's `setMuted(true).then(play())` chain retries
  // explicitly after the iframe + SDK are both ready, recovering from the
  // throttle. Only enable in `videoBackgroundMode` — otherwise the iframe
  // is loaded with `autoplay=0` and we *don't* want to force-play it.
  useVimeoForceAutoplay({
    iframeRef,
    enabled: Boolean(resolvedVimeoUrl) && videoBackgroundMode && isMuted,
    iframeSrc: heroEmbedUrl,
  });

  /**
   * Create a fresh Vimeo Player instance against the current iframe and wire
   * up the play/pause listeners that drive the overlay state. Idempotent —
   * bails when a player already exists or the SDK isn't loaded yet.
   *
   * Called from two places:
   *   1. The mount effect below (after the Vimeo SDK has loaded).
   *   2. The iframe's `onLoad` (every time the iframe document reloads —
   *      including after `handleUnmuteClick` swaps the `src` to bake in
   *      `muted=0` for the iOS Safari user-gesture chain).
   *
   * Reusing one function for both paths means the play/pause overlay regains
   * a working `playerRef.current` synchronously on iframe load, instead of
   * waiting for a setTimeout retry.
   */
  const initVimeoPlayer = useCallback(() => {
    if (typeof window === 'undefined' || !window.Vimeo) return;
    if (!iframeRef.current || playerRef.current) return;
    try {
      const player = new window.Vimeo.Player(iframeRef.current);
      playerRef.current = player;
      player.on('play', () => setIsPlaying(true));
      player.on('pause', () => setIsPlaying(false));
      player.ready().then(() => {
        if (videoBackgroundMode) setIsPlaying(true);
        // Detect the source video's natural aspect ratio so `VideoWrapper`
        // can match it — otherwise portrait videos (e.g. AquiPor's 9:16
        // hero, PD-34612) letterbox inside the default 16:9 wrapper.
        Promise.all([player.getVideoWidth(), player.getVideoHeight()])
          .then(([width, height]) => {
            if (width > 0 && height > 0) {
              setVideoAspectRatio(width / height);
            }
          })
          .catch(() => {
            /* Vimeo dimensions unavailable — keep the default 16:9. */
          });
      });
    } catch {
      /* Vimeo SDK construct failed — overlay falls back to a noop. */
    }
  }, [videoBackgroundMode]);

  useEffect(() => {
    if (!vimeoScriptLoaded || !resolvedVimeoUrl || !iframeRef.current) return undefined;
    const timer = setTimeout(initVimeoPlayer, 200);
    return () => clearTimeout(timer);
  }, [vimeoScriptLoaded, resolvedVimeoUrl, initVimeoPlayer]);

  /**
   * Unmute the hero Vimeo player by reloading the iframe with `muted=0` in
   * the URL params, instead of calling `player.setMuted(false)` + `play()`
   * via Vimeo's postMessage API.
   *
   * iOS Safari requires autoplay-with-sound to happen inside a single
   * user-gesture tick. The previous implementation chained four cross-origin
   * postMessage round-trips between the user's tap and the audible `play()`
   * call — by the time `play()` fired, iOS had already expired the gesture
   * and silently blocked the unmute, so users on real iOS Safari (silent
   * switch off, source video has audio) heard nothing.
   *
   * Swapping the iframe `src` synchronously preserves the user-gesture
   * context: Vimeo's player boots fresh with `muted=0` already in the URL,
   * no postMessage handshake required, and iOS Safari accepts the resulting
   * autoplay-with-sound. Video restarts from 0:00, matching the prior UX
   * (which also seeked to 0 on unmute). The new Vimeo Player instance is
   * recreated by `initVimeoPlayer` via the iframe's `onLoad` handler.
   */
  const handleUnmuteClick = useCallback(() => {
    setIsMuted(false);
    setIsPlaying(true);
    trackEvent('builder_hero_video_unmute', { offering_slug: slug });
    abTest?.trackConversion('video_unmute', { offering_slug: slug });

    if (iframeRef.current && resolvedVimeoUrl) {
      const unmutedUrl = buildUnmutedHeroVimeoEmbedUrl(resolvedVimeoUrl);
      if (unmutedUrl) {
        playerRef.current = null;
        iframeRef.current.src = unmutedUrl;
      }
    }
  }, [slug, abTest, resolvedVimeoUrl]);

  const handlePlayPauseClick = useCallback(async () => {
    if (playerRef.current) {
      try {
        if (isPlaying) {
          await playerRef.current.pause();
          setIsPlaying(false);
          trackEvent('builder_hero_video_pause', { offering_slug: slug });
        } else {
          await playerRef.current.play();
          setIsPlaying(true);
          trackEvent('builder_hero_video_play', { offering_slug: slug });
        }
      } catch {
        // Vimeo API error
      }
    }
  }, [isPlaying, slug]);

  const handleHeroImageLoad = useCallback((event) => {
    const img = event?.currentTarget;
    const width = img?.naturalWidth;
    const height = img?.naturalHeight;

    if (!width || !height) return;

    const nextAspectRatio = width / height;
    setHeroImageAspectRatio((currentAspectRatio) =>
      Math.abs((currentAspectRatio || 0) - nextAspectRatio) < 0.001
        ? currentAspectRatio
        : nextAspectRatio,
    );
  }, []);

  const resolvedEyebrowText = useMemo(() => {
    if (!showEyebrow) return '';
    if (eyebrowText) return eyebrowText;
    if (useDynamicData && offeringData?.name) return `Invest in ${offeringData.name}`;
    return '';
  }, [showEyebrow, eyebrowText, useDynamicData, offeringData?.name]);

  const resolvedTitle = resolveBuilderHeroCopy({
    slug,
    useDynamicData,
    dynamicValue: offeringData?.content,
    authoredValue: title,
    isAuthoredValueEmpty: !title || title === 'Your Headline Here',
  });
  const headingComponents = resolveBuilderHeroHeading({
    showEyebrow,
    eyebrowText,
    useDynamicData,
    offeringName: offeringData?.name,
  });

  const isSubtitleEmpty =
    !subtitle ||
    subtitle.replace(/<[^>]*>/g, '').trim() === '' ||
    subtitle ===
      'Your company description goes here. Explain what makes your offering unique and why investors should be interested.' ||
    subtitle === 'Join thousands of investors backing innovative companies.';
  const resolvedSubtitle = useMemo(() => {
    const raw = resolveBuilderHeroCopy({
      slug,
      useDynamicData,
      dynamicValue: offeringData?.elevatorPitch,
      authoredValue: subtitle || '',
      isAuthoredValueEmpty: isSubtitleEmpty,
    });
    // Strip truly-empty richText like "<p><br></p>" so server and client match
    if (raw && raw.replace(/<[^>]*>/g, '').trim() === '') return '';
    return raw;
  }, [slug, useDynamicData, offeringData?.elevatorPitch, isSubtitleEmpty, subtitle]);

  const endDays = useMemo(
    () => (useDynamicData && offeringData ? getTillOfferingClose(offeringData) : null),
    [getTillOfferingClose, offeringData, useDynamicData],
  );

  useEffect(() => {
    if (!subtitleRef.current || showFullSubtitle) return;
    setSubtitleOverflows(subtitleRef.current.scrollHeight > subtitleRef.current.clientHeight);
  }, [resolvedSubtitle, showFullSubtitle]);

  const renderTitle = () => {
    const processedTitle = resolvedTitle.replace(/\n/g, '|');

    const renderWithBreaks = (text) =>
      text.split('|').map((line, index, arr) => (
        <React.Fragment key={index}>
          {line}
          {index < arr.length - 1 && <br />}
        </React.Fragment>
      ));

    if (!titleHighlight || !processedTitle.includes(titleHighlight)) {
      return renderWithBreaks(processedTitle);
    }

    const parts = processedTitle.split(titleHighlight);
    return (
      <>
        {renderWithBreaks(parts[0])}
        <span style={{ color: titleHighlightColor }}>{titleHighlight}</span>
        {parts[1] && renderWithBreaks(parts[1])}
      </>
    );
  };

  const bulletList = useMemo(() => {
    if (bullets?.length) return bullets;
    if (bulletItems?.length) return bulletItems;
    return [];
  }, [bullets, bulletItems]);

  const renderBullets = () => {
    if (!showBullets || bulletList.length === 0) return null;

    return (
      <Box sx={{ mt: 3 }}>
        {bulletList.map((bullet, index) => (
          <Box
            key={`bullet-${index}-${bullet.id || ''}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mb: 2,
              gap: 1.5,
            }}
          >
            <Box
              component="span"
              sx={{
                color: bulletIconColor,
                fontSize: '20px',
                lineHeight: 1.4,
              }}
            >
              {bullet.icon || '▸'}
            </Box>
            <Typography
              sx={{
                color: bulletTextColor,
                fontSize: '16px',
                lineHeight: 1.6,
                fontFamily: effectiveBodyFont,
              }}
              dangerouslySetInnerHTML={{ __html: sanitize(bullet.text || '') }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderStatusBadges = () => {
    if (!showStatusBadges) return null;

    const dynamicBadges = [];

    if (useDynamicData && offeringData) {
      if (isOverRaised) {
        dynamicBadges.push({
          key: 'waitlist',
          label: 'WAITLIST',
          backgroundColor: '#FFEAD6',
          textColor: '#C2410C',
          tooltip:
            'This campaign has exceeded its maximum funding goal. You can still invest, but you will be added to a waitlist.',
        });
      }

      if (isTesting) {
        dynamicBadges.push({
          key: 'testing',
          label: 'TESTING THE WATERS',
          backgroundColor: '#FFE0E9',
          textColor: '#9F1239',
        });
      }

      if (isRegD) {
        dynamicBadges.push({
          key: 'regd',
          label: isQPOnly ? 'QUALIFIED PURCHASER ONLY' : 'ACCREDITED INVESTOR ONLY',
          backgroundColor: 'rgba(24, 92, 101, 0.1)',
          textColor: COLORS.primary,
        });
      }

      if (endDays && !(isMobile && isRegD)) {
        dynamicBadges.push({
          key: 'daysLeft',
          label: endDays.toUpperCase(),
          backgroundColor: '#FFE0E9',
          textColor: '#9F1239',
          tooltip:
            'Closing date is based on the most recent SEC filing and may be subject to change. This campaign may be extended further if an amendment is filed.',
        });
      }
    }

    const badges = useDynamicData ? dynamicBadges : manualStatusBadges;

    if (!badges || badges.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {badges.map((badge, index) => (
          <Box
            key={badge.key || badge.id || `${badge.label}-${index}`}
            sx={{
              backgroundColor: badge.backgroundColor || 'rgba(24, 92, 101, 0.1)',
              borderRadius: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
            }}
          >
            <Typography
              sx={{
                color: badge.textColor || COLORS.primary,
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {badge.label}
              {badge.tooltip && (
                <Tooltip title={badge.tooltip} arrow placement="top">
                  <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer', opacity: 0.8 }} />
                </Tooltip>
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderDisclaimer = () => {
    if (!showDisclaimer) return null;

    if (useDynamicData && offeringData) {
      return renderHeroDisclaimerContent(
        <DisclaimerText sx={{ color: disclaimerColor }}>
          {/* `isTtw` mirrors the orange Reserve Now CTA above — when
              `forceReserveCta` is on (gacw-reg-a) or one of the four
              auto-detected V2 TTW statuses is present, the disclaimer
              should read "Test the Waters offering" / "potential
              investment" so the button copy and the legal text don't
              disagree under the CTA. */}
          <OfferingLegalText offeringData={offeringData} isTtw={isTTW} />
        </DisclaimerText>,
        additionalDisclaimerText,
        disclaimerColor,
      );
    }

    const resolvedDisclaimerText = disclaimerLinkText || disclaimerRegType;
    const safeResolvedDisclaimerUrl = safeUrl(disclaimerLinkUrl || disclaimerRegTypeLink);
    const safeBrokerLink = safeUrl(disclaimerBrokerLink);
    const resolvedPrefix = disclaimerPrefix || 'Offered pursuant to';

    return renderHeroDisclaimerContent(
      <DisclaimerText sx={{ color: disclaimerColor }}>
        {resolvedPrefix}{' '}
        {safeResolvedDisclaimerUrl ? (
          <a
            href={safeResolvedDisclaimerUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: disclaimerLinkColor, textDecoration: 'underline' }}
          >
            {resolvedDisclaimerText}
          </a>
        ) : (
          <span>{resolvedDisclaimerText}</span>
        )}{' '}
        offering is made available through{' '}
        {safeBrokerLink ? (
          <a
            href={safeBrokerLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: disclaimerLinkColor, textDecoration: 'underline' }}
          >
            {disclaimerBrokerName}
          </a>
        ) : (
          disclaimerBrokerName
        )}
        , a member of{' '}
        <a
          href="https://www.finra.org"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: disclaimerLinkColor, textDecoration: 'underline' }}
        >
          FINRA
        </a>
        /
        <a
          href="https://www.sipc.org"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: disclaimerLinkColor, textDecoration: 'underline' }}
        >
          SIPC
        </a>
        . This investment is speculative, illiquid, and involves a high degree of risk, including
        the possible loss of your entire investment.
      </DisclaimerText>,
      additionalDisclaimerText,
      disclaimerColor,
    );
  };

  const getButtonPosition = () => {
    switch (unmuteButtonPosition) {
      case 'top-right':
        return { top: 16, right: 16 };
      case 'top-left':
        return { top: 16, left: 16 };
      case 'bottom-right':
        return { bottom: 16, right: 16 };
      case 'bottom-left':
        return { bottom: 16, left: 16 };
      case 'center':
      default:
        return {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 'auto',
        };
    }
  };

  const renderPriceInfo = () => {
    if (!showPriceInfo) return null;
    if (!showSharePrice && !showMinInvestment) return null;

    return (
      <PriceInfo sx={{ color: priceInfoColor }}>
        {showSharePrice && (
          <Box>
            <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)' }}>
              {sharePriceLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '20px' },
                fontWeight: 600,
                fontFamily: "'owners-wide', sans-serif",
                color: priceInfoColor,
              }}
            >
              {sharePriceValue}
            </Typography>
          </Box>
        )}
        {showSharePrice && showMinInvestment && (
          <Box
            sx={{
              width: '1px',
              height: '40px',
              backgroundColor: 'rgba(0,0,0,0.15)',
              mx: { xs: 2, md: 3 },
            }}
          />
        )}
        {showMinInvestment && (
          <Box>
            <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)' }}>
              {minInvestmentLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '20px' },
                fontWeight: 600,
                fontFamily: "'owners-wide', sans-serif",
                color: priceInfoColor,
              }}
            >
              {minInvestmentValue}
            </Typography>
          </Box>
        )}
      </PriceInfo>
    );
  };

  const isBuilderEditor = builder?.isEditing || builder?.isPreviewing;

  const renderEarlyBird = () => {
    if (!showEarlyBird) return null;

    const hasDeadline = Boolean(resolvedEarlyBirdDeadline);

    if (!hasDeadline && !isBuilderEditor) return null;

    if (hasDeadline) {
      const target = new Date(resolvedEarlyBirdDeadline);
      if (!Number.isNaN(target.getTime()) && target.getTime() <= Date.now() && !isBuilderEditor) {
        return null;
      }
    }

    return (
      <Box
        sx={{
          borderRadius: '12px',
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 2.5 },
          maxWidth: '560px',
          background: earlyBirdBackground,
          border: '1px solid rgba(255,255,255,0.1)',
        }}
        data-cy="hero-early-bird"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
                px: 1,
                py: 0.25,
                borderRadius: '999px',
                background: earlyBirdBadgeBackground,
                color: earlyBirdBadgeColor,
                whiteSpace: 'nowrap',
              }}
            >
              {earlyBirdBadgeText}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', color: earlyBirdTextColor }}>
            {earlyBirdText}{' '}
            <strong style={{ color: earlyBirdHighlightColor }}>{resolvedEarlyBirdHighlight}</strong>
          </Typography>
        </Box>
        {resolvedEarlyBirdDeadline && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[
              { val: countdown.days, label: 'D' },
              { val: countdown.hours, label: 'H' },
              { val: countdown.minutes, label: 'M' },
              { val: countdown.seconds, label: 'S' },
            ].map((unit) => (
              <CountdownUnit
                key={unit.label}
                value={unit.val}
                label={unit.label}
                background={earlyBirdCountdownBackground}
                color={earlyBirdCountdownColor}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const resolveStatValue = useCallback(
    (item) => {
      if (!useDynamicData || !offeringData) return item.value;

      switch (item.dataKey) {
        case 'previouslyCrowdfunded': {
          const prev =
            Number.parseFloat(offeringData?.previousRaiseTotal || 0) +
            Number.parseFloat(offeringData?.previousCrowdfundedRaiseTotal || 0);
          return prev > 0 ? formatCurrencyFlexible(prev) : item.value;
        }
        case 'currentRaise':
          return currentAmountRaised
            ? formatCurrencyFlexible(Number.parseFloat(currentAmountRaised))
            : item.value;
        case 'investorCount': {
          const count = currentInvestorCount;
          if (!count) return item.value;
          return Number(count) >= 10000
            ? `${(Number(count) / 1000).toFixed(0)}K+`
            : Number(count).toLocaleString();
        }
        case 'valuation': {
          const val = offeringData?.valuation;
          if (!val) return item.value;
          const num = Number.parseFloat(val);
          const stripZeros = (s) => s.replace(/\.00$/, '');
          if (num >= 1_000_000_000) return `$${stripZeros((num / 1_000_000_000).toFixed(2))}B`;
          if (num >= 1_000_000) return `$${stripZeros((num / 1_000_000).toFixed(2))}M`;
          return formatCurrency(num, 0);
        }
        case 'minInvestment': {
          const min = offeringData?.minimumInvestmentAmount;
          if (!min) return item.value;
          const minNum = typeof min === 'string' ? Number.parseFloat(min) : min;
          return formatCurrency(minNum, Math.round(minNum) === minNum ? 0 : 2);
        }
        case 'fundingGoal': {
          const goal = offeringData?.maximumFundingAmount;
          if (!goal) return item.value;
          return formatCurrencyFlexible(Number.parseFloat(goal));
        }
        default:
          return item.value;
      }
    },
    [useDynamicData, offeringData, currentAmountRaised, currentInvestorCount, formatCurrency],
  );

  const hasStatData = useCallback(
    (item) => {
      if (!useDynamicData || !offeringData) return true;
      switch (item.dataKey) {
        case 'previouslyCrowdfunded': {
          const prev =
            Number.parseFloat(offeringData?.previousRaiseTotal || 0) +
            Number.parseFloat(offeringData?.previousCrowdfundedRaiseTotal || 0);
          return prev > 0;
        }
        case 'currentRaise':
          return canShowAmountRaised;
        case 'investorCount':
          return (
            (shouldShowAmount ||
              (!hideInvestors && !hideValue && currentAmountRaisedNumber >= 124000)) &&
            Boolean(currentInvestorCount)
          );
        case 'valuation':
          return Boolean(offeringData?.valuation);
        case 'minInvestment':
          return Boolean(offeringData?.minimumInvestmentAmount);
        case 'fundingGoal':
          return Boolean(offeringData?.maximumFundingAmount);
        default:
          return true;
      }
    },
    [
      useDynamicData,
      offeringData,
      canShowAmountRaised,
      shouldShowAmount,
      hideInvestors,
      hideValue,
      currentAmountRaisedNumber,
      currentInvestorCount,
    ],
  );

  const renderStatsGrid = () => {
    if (!showStatsGrid || !statsGridItems?.length) return null;

    const visibleItems = statsGridItems.filter(
      (item) => item.hideWhenEmpty !== true || hasStatData(item),
    );
    if (!visibleItems.length) return null;

    return (
      <StatsGrid data-cy="hero-stats-grid">
        {visibleItems.map((item, index) => (
          <StatCard
            key={`stat-${index}-${item.dataKey || item.label}`}
            sx={{
              backgroundColor: statsGridBackgroundColor,
              borderColor: statsGridBorderColor,
              border:
                statsGridBorderColor !== 'transparent'
                  ? `1px solid ${statsGridBorderColor}`
                  : 'none',
            }}
          >
            <Typography
              sx={{
                color: statsGridLabelColor,
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.02em',
                mb: 0.5,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                color: statsGridValueColor,
                fontSize: '18px',
                fontWeight: 600,
                lineHeight: 1.2,
                fontFamily: "'owners-wide', sans-serif",
              }}
            >
              {resolveStatValue(item)}
            </Typography>
          </StatCard>
        ))}
      </StatsGrid>
    );
  };

  const contentHorizontalPadding = useMemo(() => {
    const parts = contentPadding.trim().split(/\s+/);
    if (parts.length >= 2) return parts[1];
    return parts[0];
  }, [contentPadding]);

  const parsedBannerPadding = useMemo(() => {
    const parts = raisedBannerPadding.trim().split(/\s+/);
    if (parts.length === 4) return { top: parts[0], right: parts[1], bottom: parts[2] };
    if (parts.length === 3) return { top: parts[0], right: parts[1], bottom: parts[2] };
    if (parts.length === 2) return { top: parts[0], right: parts[1], bottom: parts[0] };
    return { top: parts[0], right: parts[0], bottom: parts[0] };
  }, [raisedBannerPadding]);

  const heroImageSizes = `(max-width: 768px) 100vw, ${mediaWidthPercent}vw`;

  const dynamicHeroThumbnail = useDynamicData
    ? offeringData?.coverDesktopUrl
    : heroImageData?.coverDesktopUrl;
  const resolvedVideoThumbnail =
    useDynamicHeroImage && dynamicHeroThumbnail ? dynamicHeroThumbnail : videoThumbnail;

  const hasVideo = showVideo && resolvedVimeoUrl;

  const dynamicVideoUrl = useDynamicData ? offeringData?.videoUrl : '';
  const hasClickToPlayVideo = !hasVideo && dynamicVideoUrl;
  const heroImageSizing =
    !hasVideo && showHeroImage && resolvedVideoThumbnail
      ? resolveHeroImageSizing(heroImageAspectRatio)
      : null;
  const usesNaturalHeroImageSizing = Boolean(heroImageSizing);
  const heroImageObjectFit = usesNaturalHeroImageSizing ? 'contain' : 'cover';

  useEffect(() => {
    setHeroImageAspectRatio(null);
  }, [resolvedVideoThumbnail]);

  const isHydrated = useIsHydrated();

  if (!isHydrated) return null;

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    const ytMatch = url.match(
      /(?:youtube\.com(?:\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|\/)|youtu\.be\/)([^&\n]{11})/,
    );
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    return '';
  };

  const renderVideoPlayer = () => {
    if (!resolvedVimeoUrl) {
      if (!resolvedVideoThumbnail) {
        return (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundColor: '#F3F4F6',
              borderRadius: videoBorderRadius,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            data-cy="hero-image-placeholder"
          />
        );
      }

      return (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            cursor: hasClickToPlayVideo ? 'pointer' : 'default',
          }}
          onClick={hasClickToPlayVideo ? () => setVideoModalOpen(true) : undefined}
        >
          <Image
            src={resolvedVideoThumbnail}
            alt="Hero image"
            width={1200}
            height={675}
            priority={true}
            fetchPriority="high"
            sizes={heroImageSizes}
            onLoad={handleHeroImageLoad}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: heroImageObjectFit,
              borderRadius: videoBorderRadius,
            }}
          />
          {hasClickToPlayVideo && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                borderRadius: '50%',
                background:
                  'linear-gradient(126.6deg, rgba(241, 241, 241, 0.222) 28.69%, rgba(221, 221, 221, 0.12) 100%)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.8px solid rgba(255, 255, 255, 0.6)',
                zIndex: 2,
                '&:hover': { transform: 'translate(-50%, -50%) scale(1.08)' },
                transition: 'transform 0.2s ease',
              }}
              data-cy="hero-play-button"
            >
              <PlayArrowIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#FFFFFF' }} />
            </Box>
          )}
        </Box>
      );
    }

    // Click-to-play hero (videoBackgroundMode=false) — replace Vimeo's
    // small idle poster with our own full-bleed thumbnail until the
    // user actually wants to play. The poster comes from the offering
    // cover image (or `videoThumbnail` override) and uses the same
    // `object-fit: cover` rule as the image-only hero so it fills
    // exactly the rectangle the iframe will occupy after click.
    if (!videoBackgroundMode && !videoStarted) {
      return (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
            backgroundColor: '#000',
          }}
          onClick={() => {
            setVideoStarted(true);
            trackEvent('builder_hero_video_play_click', { offering_slug: slug });
            abTest?.trackConversion('hero_play_click', { offering_slug: slug });
          }}
          role="button"
          tabIndex={0}
          aria-label="Play hero video"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setVideoStarted(true);
            }
          }}
          data-cy="hero-click-to-play-thumbnail"
        >
          {resolvedVideoThumbnail && (
            <Image
              src={resolvedVideoThumbnail}
              alt="Hero video thumbnail"
              width={1200}
              height={675}
              priority={true}
              fetchPriority="high"
              sizes={heroImageSizes}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                borderRadius: videoBorderRadius,
              }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              borderRadius: '50%',
              background:
                'linear-gradient(126.6deg, rgba(241, 241, 241, 0.222) 28.69%, rgba(221, 221, 221, 0.12) 100%)',
              backdropFilter: 'blur(5px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.8px solid rgba(255, 255, 255, 0.6)',
              zIndex: 2,
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translate(-50%, -50%) scale(1.08)' },
            }}
          >
            <PlayArrowIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#FFFFFF' }} />
          </Box>
        </Box>
      );
    }

    return (
      <>
        <iframe
          ref={iframeRef}
          id={iframeId}
          src={heroEmbedUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Video"
          onLoad={initVimeoPlayer}
          style={{
            borderRadius: videoBorderRadius,
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />

        {showUnmuteButton && videoBackgroundMode && (
          <VideoOverlay>
            {isMuted ? (
              <UnmuteButton
                onClick={handleUnmuteClick}
                role="button"
                tabIndex={0}
                aria-label="Unmute and play from start"
                onKeyDown={(e) => e.key === 'Enter' && handleUnmuteClick()}
                sx={getButtonPosition()}
                data-cy="hero-unmute-button"
              >
                <VolumeOffIcon sx={{ fontSize: 32 }} />
              </UnmuteButton>
            ) : (
              <UnmuteButton
                onClick={handlePlayPauseClick}
                role="button"
                tabIndex={0}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
                onKeyDown={(e) => e.key === 'Enter' && handlePlayPauseClick()}
                sx={{
                  bottom: 16,
                  right: 16,
                  top: 'auto',
                  left: 'auto',
                  margin: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#FFFFFF',
                  width: 48,
                  height: 48,
                }}
                data-cy="hero-playpause-button"
              >
                {isPlaying ? (
                  <PauseIcon sx={{ fontSize: 24 }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 24 }} />
                )}
              </UnmuteButton>
            )}
          </VideoOverlay>
        )}
      </>
    );
  };

  const shouldRenderHeroMedia = (showVideo && resolvedVimeoUrl) || showHeroImage;

  return (
    <>
      {resolvedVimeoUrl && (
        <Script
          src="https://player.vimeo.com/api/player.js"
          strategy="afterInteractive"
          onLoad={() => setVimeoScriptLoaded(true)}
        />
      )}

      <HeroContainer
        id={sectionId || undefined}
        sx={{
          backgroundColor,
          backgroundImage: backgroundImage
            ? `${hasGradient ? backgroundGradientValue : `linear-gradient(${backgroundOverlayColor}, ${backgroundOverlayColor})`}, url(${backgroundImage})`
            : hasGradient
              ? backgroundGradientValue
              : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          fontFamily: effectiveBodyFont,
          ...sectionBorderRadiusSx(sectionBorderRadius),
        }}
        data-cy="builder-hero-section"
      >
        {showRaisedBanner && canShowAmountRaised && raisedBannerPosition === 'top' && (
          <RaisedBanner
            sx={{
              background: raisedBannerGradient || raisedBannerBackgroundColor,
              color: raisedBannerTextColor,
              paddingTop: parsedBannerPadding.top,
              paddingBottom: parsedBannerPadding.bottom,
              paddingRight: parsedBannerPadding.right,
              paddingLeft: `max(${contentHorizontalPadding}, calc((100vw - ${maxWidth}px) / 2 + ${contentHorizontalPadding}))`,
              marginTop: raisedBannerMarginTopMobile
                ? { xs: raisedBannerMarginTopMobile, md: raisedBannerMarginTop }
                : raisedBannerMarginTop,
              marginBottom: raisedBannerMarginBottomMobile
                ? { xs: raisedBannerMarginBottomMobile, md: raisedBannerMarginBottom }
                : raisedBannerMarginBottom,
              fontSize: { xs: raisedBannerFontSize, md: '16px' },
              fontWeight: raisedBannerFontWeight,
              letterSpacing: raisedBannerLetterSpacing,
              borderRadius: raisedBannerBorderRadius,
              fontFamily: "'owners-wide', sans-serif",
            }}
            data-cy="hero-raised-banner"
          >
            {dynamicRaisedBannerText}
          </RaisedBanner>
        )}

        <ContentWrapper
          sx={{
            maxWidth: `${maxWidth}px`,
            padding: { xs: contentPaddingMobile, md: contentPadding },
            marginTop: contentTopSpacing,
            gridTemplateColumns: {
              xs: '1fr',
              md: `${100 - mediaWidthPercent}fr ${mediaWidthPercent}fr`,
            },
            gap: { xs: contentGapMobile, md: contentGapDesktop },
            ...(showRaisedBanner &&
              canShowAmountRaised &&
              raisedBannerPosition === 'top' && {
                paddingTop: '16px',
              }),
          }}
        >
          <LeftContent sx={{ gap: 0 }}>
            {renderStatusBadges()}
            {resolvedEyebrowText && (
              <Typography
                component={headingComponents.eyebrowComponent}
                sx={{
                  color: eyebrowColor,
                  fontFamily: effectiveBodyFont,
                  fontSize: eyebrowFontSize,
                  fontWeight: eyebrowFontWeight,
                  letterSpacing: eyebrowLetterSpacing,
                  textTransform: eyebrowTextTransform,
                  mb: `${eyebrowMarginBottom}px`,
                }}
                data-cy="hero-eyebrow-text"
              >
                {resolvedEyebrowText}
              </Typography>
            )}
            <Typography
              variant="h1"
              component={headingComponents.titleComponent}
              sx={{
                color: titleColor,
                fontFamily: effectiveTitleFont,
                fontSize: titleMobileFontSize
                  ? { xs: titleMobileFontSize, md: titleFontSize }
                  : titleFontSize,
                fontWeight: titleFontWeight,
                lineHeight: Number(titleLineHeight) || 1.15,
                mb: { xs: 1.5, md: 3 },
                // `word-break: normal` + `overflow-wrap: anywhere` is
                // the spec-correct way to say "break at whitespace
                // first; if a single word is wider than the line, break
                // it anywhere as a last resort". Crucially, `anywhere`
                // (unlike `break-word`) contributes a 0 to min-content
                // intrinsic sizing, so the H1's grid-item parent can
                // actually shrink to the viewport width — without this,
                // a long display title like "Autonomous" pushes the
                // grid track wider than the viewport, gets clipped by
                // the HeroContainer's `overflow: hidden`, and the
                // viewer sees the word cut off on the right. PD-000
                // / Greenfield Robotics Jun 2026.
                wordBreak: 'normal',
                overflowWrap: 'anywhere',
                hyphens: 'manual',
              }}
            >
              {renderTitle()}
              {titleSuperscript && <sup>{titleSuperscript}</sup>}
            </Typography>

            <Box
              ref={subtitleRef}
              sx={{
                color: subtitleColor,
                fontFamily: effectiveBodyFont,
                fontSize: { xs: '16px', md: subtitleFontSize },
                fontWeight: subtitleFontWeight,
                lineHeight: Number(subtitleLineHeight) || 1.6,
                maxWidth: '600px',
                mb: { xs: 0.5, md: 1.5 },
                '& p': { margin: 0 },
                ...(!showFullSubtitle && {
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
              }}
              dangerouslySetInnerHTML={{ __html: resolvedSubtitle }}
            />
            {(subtitleOverflows || showFullSubtitle) && (
              <Typography
                onClick={() => setShowFullSubtitle((p) => !p)}
                sx={{
                  color: COLORS.primary,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  mb: 1.5,
                }}
              >
                {showFullSubtitle ? 'Show less' : 'Show more'}
              </Typography>
            )}

            {renderBullets()}

            <InvestButtonWrapper sx={{ mb: 0.5 }}>
              {/* When `useDynamicData` is on, the button text is
               * derived from offeringData (Get Equity for Reg D, Get
               * Shares Now otherwise, Join the Waitlist when over
               * raised, etc.). Don't render the button until that
               * data is in hand — otherwise it briefly flashes the
               * default `investButtonText` and then jumps to the
               * correct copy. Worse, on launch-day status transitions
               * (TTW → approved) a stale React Query cache could
               * keep the wrong copy visible for minutes (EVOQ Bio
               * Jun 2 2026, Adam Gunn report — saw "Reserve Now"
               * for ~5 min after launch). `useGetOffering`'s new
               * `refetchOnMount: 'always'` handles the cache-stale
               * case; this gate handles the initial-load flicker. */}
              {(!useDynamicData || offeringData) && (
                <Button
                  onClick={handleInvestClick}
                  className="startengine-button"
                  sx={{
                    // Background priority (highest → lowest):
                    //   1. Over-raised → waitlist color
                    //   2. TTW status   → reserve color (same #E06004 as
                    //      the sidebar's Reserve Now button — Danny
                    //      Rickard report Jun 3 2026, Artly TTW launched
                    //      with hero text correctly flipped to "Reserve
                    //      Now" but background still showed the issuer's
                    //      brand teal `investButtonBackgroundColor`).
                    //   3. Default      → issuer-configured invest color
                    backgroundColor: isOverRaised
                      ? waitlistButtonBackgroundColor
                      : isTTW
                        ? reserveButtonBackgroundColor
                        : investButtonBackgroundColor,
                    color: investButtonTextColor,
                    borderRadius: investButtonBorderRadius,
                    fontSize: investButtonFontSize,
                    fontWeight: investButtonFontWeight,
                    fontFamily: effectiveBodyFont,
                    width: { xs: '100%', md: investButtonWidth },
                    height: '48px',
                    padding: investButtonPadding,
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: isOverRaised
                        ? waitlistButtonBackgroundColor
                        : isTTW
                          ? reserveButtonBackgroundColor
                          : investButtonBackgroundColor,
                      opacity: 0.9,
                    },
                  }}
                  data-cy="hero-invest-button"
                >
                  {resolvedButtonText}
                </Button>
              )}

              {renderPriceInfo()}
            </InvestButtonWrapper>

            {renderEarlyBird()}

            {renderDisclaimer()}

            {isMobile && offeringData && (
              <Box sx={{ mt: 2 }}>
                <CriteriaBadge offering={offeringData} />
              </Box>
            )}
          </LeftContent>

          {shouldRenderHeroMedia && (
            <RightContent>
              <VideoWrapper
                sx={{
                  borderRadius: videoBorderRadius,
                  // Frame defaults are scoped by *render intent*:
                  //
                  //   - Autoplaying background video
                  //     (`videoBackgroundMode=true`): keep the 12px
                  //     polaroid frame + soft shadow. That's how every
                  //     existing campaign hero looks today (CancerVAX,
                  //     AquiPor, etc.) and the frame is an intentional
                  //     part of the "video pops off the page" look.
                  //   - Click-to-play video
                  //     (`videoBackgroundMode=false`, autoplay=0):
                  //     drop the frame. The Vimeo iframe sits on its
                  //     own thumbnail + play button — wrapping that in
                  //     a polaroid frame makes the play button look
                  //     awkwardly inset. Greenfield Robotics (Jun 2026)
                  //     is the first campaign to use this mode.
                  //   - Image-only (`showVideo=false`): drop the frame.
                  //     Same edge-to-edge intent as click-to-play.
                  //
                  // Authors can still pull the frame back on any of
                  // these paths by setting `videoFrameWidth` to a
                  // non-zero value explicitly in Builder.
                  ...(hasVideo && videoBackgroundMode
                    ? {
                        backgroundColor: videoFrameColor,
                        border: `${videoFrameWidth} solid ${videoFrameColor}`,
                        boxShadow: videoFrameShadow,
                      }
                    : {
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                      }),
                  overflow: 'hidden',
                  ...(hasVideo ? resolveHeroVideoSizing(videoAspectRatio) || {} : {}),
                  ...(!hasVideo ? heroImageSizing || {} : {}),
                  // Image-only heroes default to the same 16:9 box as
                  // videos, but some issuer/private-fund cover images
                  // include disclosure text near the bottom edge. When
                  // the loaded image is taller than 16:9, honor its
                  // natural ratio so that text is not cropped out of
                  // the rounded media frame.
                }}
                data-cy="builder-hero-video-wrapper"
              >
                {hasVideo ? renderVideoPlayer() : null}
                {!resolvedVimeoUrl && showHeroImage ? renderVideoPlayer() : null}
              </VideoWrapper>
              {showRaisedBanner && canShowAmountRaised && raisedBannerPosition === 'belowMedia' && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: usesNaturalHeroImageSizing ? { xs: '12px', md: '16px' } : '-24px',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: raisedBannerBackgroundColor,
                      color: raisedBannerTextColor,
                      padding: '0 24px',
                      fontSize: { xs: '18px', sm: '24px', lg: '28px' },
                      fontWeight: raisedBannerFontWeight,
                      letterSpacing: raisedBannerLetterSpacing,
                      borderRadius: usesNaturalHeroImageSizing ? '16px' : '0px 0px 16px 16px',
                      boxShadow: usesNaturalHeroImageSizing
                        ? '0px 6px 18px rgba(0, 0, 0, 0.12)'
                        : '-2px -2px 10px rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                      height: { xs: '32px', lg: '47px' },
                      maxWidth: '100%',
                    }}
                    data-cy="hero-raised-banner"
                  >
                    {dynamicRaisedBannerText}
                  </Box>
                </Box>
              )}
              {renderStatsGrid()}
            </RightContent>
          )}
        </ContentWrapper>
      </HeroContainer>

      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (userData) => {
          if (userData?.event === 'signup' && typeof window !== 'undefined') {
            const search = window.location.search || '';
            window.location.href = appendPostHogDistinctId(`${redirectUrl}${search}`);
          }
        },
        onSuccessfulLogin: (response) => {
          if (response?.error) return;

          const pageType = isTTW ? 'reserve page' : 'invest page';

          if (typeof window !== 'undefined') {
            window.dataLayer?.push({
              event: 'investNowClick',
              id: offeringData?.slug,
              name: offeringData?.name,
              category: offeringData?.regulationType,
              pageType,
              customer_id: user?.primaryUserId,
              ecommerce: {
                items: [
                  {
                    item_id: offeringData?.slug,
                    item_name: offeringData?.name,
                    item_category: offeringData?.regulationType,
                    price: offeringData?.pricePerShare
                      ? Number.parseFloat(offeringData.pricePerShare)
                      : undefined,
                    quantity: 1,
                    currency: 'USD',
                  },
                ],
              },
            });

            setTimeout(() => {
              const search = window.location.search || '';
              window.location.href = appendPostHogDistinctId(`${redirectUrl}${search}`);
            }, 250);
          }
        },
      })}

      {hasClickToPlayVideo && (
        <Modal
          open={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(8px)',
            },
          }}
          data-cy="hero-video-modal"
        >
          <Box
            sx={{
              width: { xs: '95vw', sm: '80vw', md: '900px' },
              aspectRatio: '16 / 9',
              outline: 'none',
            }}
          >
            {videoModalOpen && (
              <iframe
                src={getVideoEmbedUrl(dynamicVideoUrl)}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
                title="Video"
              />
            )}
          </Box>
        </Modal>
      )}
    </>
  );
};

export default BuilderHeroSection;
